/**
 * =============================================================================
 * POST-INSTALL SUCCESS SCREEN
 * =============================================================================
 *
 * Rendered after the OAuth install handshake (`/api/oauth/auth`) finishes and
 * the worker redirects the browser to
 *   `${ADMIN_DASHBOARD_URL}?store=<hash>&installed=true`
 *
 * The redirect deliberately carries no `signed_payload_jwt` because at that
 * point in the BigCommerce app lifecycle, BC has not handed us one — that
 * happens later, when the merchant opens the app from the BC control panel
 * and BC fires `/api/oauth/load?signed_payload_jwt=…`.
 *
 * Without a token, the regular dashboard (analytics + question queue) cannot
 * make authenticated API calls, so it would fall back to a generic
 * "please log in through BigCommerce" message. That looks like an error to
 * a merchant who just clicked Install. This screen replaces that fallback
 * with a clear "you're done — here's how to actually open the app" message.
 *
 * Caller contract:
 *   - Show this screen ONLY when the URL says `installed=true&store=<hash>`
 *     AND the SessionProvider reports unauthenticated. If a JWT is present
 *     (e.g. the merchant deep-linked through `/api/oauth/load`), the regular
 *     dashboard renders instead.
 *
 * =============================================================================
 */

import React from 'react';
import { Box, Panel, H2, Text } from '@bigcommerce/big-design';

export interface InstalledSuccessScreenProps {
  /**
   * Store hash extracted from the `?store=` query param. Optional — the
   * screen still renders a useful message without it.
   */
  storeHash: string | null;
}

export function InstalledSuccessScreen({
  storeHash,
}: InstalledSuccessScreenProps): React.ReactElement {
  return (
    <Box padding="large" data-testid="installed-success-screen">
      <Panel>
        <Box padding="large">
          <H2>Find My Part installed</H2>

          <Text data-testid="installed-success-store-line">
            {storeHash
              ? `The app was installed successfully on store ${storeHash}.`
              : 'The app was installed successfully.'}
          </Text>

          <Text marginTop="medium">
            To open the dashboard, return to your BigCommerce control panel
            and click <strong>Apps → Find My Part</strong>. BigCommerce will
            sign you into the dashboard with a fresh session token at that
            point.
          </Text>

          <Text marginTop="medium" color="secondary">
            This screen has no signed token attached, which is expected: the
            install callback finishes before BigCommerce hands the dashboard
            a JWT. Reaching this view directly via URL will not show
            questions until you re-enter through BigCommerce.
          </Text>
        </Box>
      </Panel>
    </Box>
  );
}

/**
 * Returns true when the current URL is the post-install redirect target
 * (`installed=true&store=<non-empty hash>`).
 *
 * Defaults to reading `window.location.search` so callers normally don't pass
 * an argument; the parameter is here so tests can pass a synthetic search
 * string without monkey-patching `window.location`.
 */
export function isPostInstallRedirect(
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): boolean {
  const params = new URLSearchParams(search);
  return params.get('installed') === 'true' && Boolean(params.get('store'));
}

/**
 * Returns the `store` query param from the URL (or the supplied search
 * string), or null if absent. Mirrors the shape used by isPostInstallRedirect.
 */
export function postInstallStoreHash(
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): string | null {
  return new URLSearchParams(search).get('store');
}
